/**
 * Decide which accompaniments are appropriate for a given main dish.
 *
 * Rules:
 *  - Rice-based mains (biryani, pulao, curd rice, lemon rice, etc.) →
 *    no accompaniment needed (already complete). Returns []  → caller hides the wheel.
 *  - South-Indian breakfasts that already include their own carb (dosa, idli,
 *    vada, uttapam, appam, pongal, upma, poha) → also no accompaniment.
 *  - Gravy/curry mains (anything with "curry", "masala", "kadhai", "korma",
 *    "rogan", "vindaloo", paneer/chicken/mutton/fish gravies, dal, sambhar,
 *    rasam) → suggest steamed/jeera RICE + ROTIS/BHAKRI.  Curd Rice excluded.
 *  - Dry sabzis / fry / tikka / kebab → ROTIS/BHAKRI only (no rice needed).
 *  - Anything else → all accompaniments allowed.
 *
 * @param {{name?: string}} dish    The main dish the user just spun.
 * @param {Array<{name: string}>} accompaniments  Full list of accompaniments.
 * @returns {Array}  Filtered list (possibly empty).
 */
export function getPairingCandidates(dish, accompaniments) {
  if (!dish || !accompaniments?.length) return [];
  const n = (dish.name || '').toLowerCase();

  // 1. Rice-based or already-complete one-pot meals → no need to pair
  const isRiceBased = /\b(biryani|pulao|pulav|fried rice|curd rice|lemon rice|tamarind rice|tomato rice|coconut rice|jeera rice|veg rice|chitra anna|bisi bele|khichdi|tehri|tahari|kadhi chawal)\b/.test(n);
  const isSouthBreakfast = /\b(dosa|idli|vada|uttapam|uttappam|appam|pongal|upma|poha|paniyaram|sevai|idiyappam|puttu)\b/.test(n);
  const isComplete = /\b(thali|combo|wrap|roll|burger|pizza|pasta|maggi|sandwich|toast|paratha|stuffed paratha|kulcha)\b/.test(n);
  if (isRiceBased || isSouthBreakfast || isComplete) return [];

  // 2. Bucket the accompaniments
  const rices = accompaniments.filter((a) =>
    /steamed rice|jeera rice/i.test(a.name)
  );
  const breads = accompaniments.filter((a) =>
    /chapati|roti|bhakri|naan|paratha/i.test(a.name)
  );
  const curdRice = accompaniments.filter((a) => /curd rice/i.test(a.name));

  // 3. Gravy / curry → rice + breads (NO curd rice)
  const isGravy = /\b(curry|masala|gravy|kadhai|kadai|korma|rogan|vindaloo|tikka masala|butter masala|makhani|do pyaza|jalfrezi|sabzi gravy|dal|daal|sambhar|sambar|rasam|kheema|kofta|chettinad|moilee|stew)\b/.test(n);
  if (isGravy) {
    return [...rices, ...breads];
  }

  // 4. Dry sabzi / fry / tikka / kebab → breads only (rice optional)
  const isDryDish = /\b(dry|sukha|fry|fried|tikka|kebab|tandoori|bhurji|bharta|65|chilli|manchurian|stir fry|jeera (?!rice)|65)\b/.test(n);
  if (isDryDish) {
    return [...breads, ...rices];
  }

  // 5. Default: anything not curd-rice (curd rice is too quirky as a default)
  return accompaniments.filter((a) => !curdRice.some((c) => c.name === a.name));
}
